import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Lock, User, Building2, Phone, ArrowRight, MailCheck, Stethoscope, IdCard } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { useAuth, homeForRole } from "@/store/AuthContext";

const PLANS = [
  { id: "solo", label: "Solo", desc: "Médico individual", price: "R$149/mês" },
  { id: "basic", label: "Basic", desc: "Clínica · 1 secretária", price: "R$399/mês" },
  { id: "max", label: "Max", desc: "Clínica · ilimitado", price: "R$899/mês" },
];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [plan, setPlan] = useState("basic");
  const [form, setForm] = useState({ name: "", clinic_name: "", email: "", phone: "", crm: "", uf: "", specialty: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [role, setRole] = useState("admin");

  const isSolo = plan === "solo";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) return toast.error("Preencha nome, e-mail e senha.");
    if (form.password.length < 6) return toast.error("A senha deve ter ao menos 6 caracteres.");
    if (!form.phone) return toast.error("Informe o telefone/WhatsApp.");
    if (!form.crm) return toast.error("Informe o número do CRM.");
    if (!isSolo && !form.clinic_name) return toast.error("Informe o nome da clínica.");
    setLoading(true);
    try {
      const d = await register({ ...form, intended_plan: plan });
      setRole(d.user?.role || (isSolo ? "medico" : "admin"));
      setPreview(d.email_preview);
    } catch (err) {
      toast.error("Não foi possível cadastrar", { description: err.message });
    } finally {
      setLoading(false);
    }
  };

  const goIn = () => { setPreview(null); navigate(homeForRole(role), { replace: true }); };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-blue-950 px-4 py-5">
      <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} className="w-full max-w-md">
        <div className="rounded-2xl border border-white/10 bg-white p-5 shadow-2xl sm:p-6">
          <div className="flex flex-col items-center text-center">
            <img src="/veloclin-logo.png" alt="VeloClin" data-testid="brand-logo" className="h-12 w-auto" />
            <h1 className="mt-2 font-heading text-xl font-extrabold tracking-tight text-slate-900">Teste grátis por 14 dias</h1>
            <p className="text-xs text-slate-500">Escolha o plano que quer testar · sem cartão</p>
          </div>

          <div className="mt-4 grid grid-cols-3 gap-2" data-testid="plan-selector">
            {PLANS.map((p) => (
              <button key={p.id} type="button" data-testid={`plan-${p.id}`} onClick={() => setPlan(p.id)}
                className={`rounded-xl border p-2 text-left transition-colors ${plan === p.id ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
                <span className={`block text-sm font-bold ${plan === p.id ? "text-blue-700" : "text-slate-800"}`}>{p.label}</span>
                <span className="block text-[10px] leading-tight text-slate-500">{p.desc}</span>
                <span className="mt-0.5 block text-[10px] font-semibold text-slate-400">{p.price}</span>
              </button>
            ))}
          </div>

          <form onSubmit={submit} className="mt-4 space-y-2.5">
            <Field icon={User} label={isSolo ? "Seu nome (médico)" : "Seu nome"} testid="reg-name-input" value={form.name} onChange={(v) => set("name", v)} placeholder={isSolo ? "Dra. Ana Souza" : "Responsável"} />
            {!isSolo && (
              <Field icon={Building2} label="Nome da clínica" testid="reg-clinic-input" value={form.clinic_name} onChange={(v) => set("clinic_name", v)} placeholder="Clínica Vida" />
            )}
            <Field icon={Mail} label="E-mail (será seu login)" testid="reg-email-input" type="email" value={form.email} onChange={(v) => set("email", v)} placeholder="voce@clinica.com" />
            <div className="grid grid-cols-2 gap-2.5">
              <Field icon={Phone} label="Telefone / WhatsApp" testid="reg-phone-input" value={form.phone} onChange={(v) => set("phone", v)} placeholder="(11) 90000-0000" />
              <Field icon={IdCard} label="CRM (validado)" testid="reg-crm-input" value={form.crm} onChange={(v) => set("crm", v)} placeholder="123456" />
            </div>
            {isSolo && (
              <div className="grid grid-cols-2 gap-2.5">
                <Field icon={Stethoscope} label="Especialidade" testid="reg-specialty-input" value={form.specialty} onChange={(v) => set("specialty", v)} placeholder="Cardiologia" />
                <Field icon={IdCard} label="UF do CRM" testid="reg-uf-input" value={form.uf} onChange={(v) => set("uf", v.toUpperCase().slice(0, 2))} placeholder="SP" />
              </div>
            )}
            <Field icon={Lock} label="Crie uma senha" testid="reg-password-input" type="password" value={form.password} onChange={(v) => set("password", v)} placeholder="mínimo 6 caracteres" />

            <button data-testid="reg-submit-btn" type="submit" disabled={loading} className="group flex w-full items-center justify-center gap-2 rounded-xl bg-blue-600 py-3 text-sm font-bold text-white shadow-[0_8px_24px_rgba(37,99,235,0.4)] transition-colors hover:bg-blue-700 disabled:opacity-60">
              {loading ? "Criando conta..." : "Começar meu teste de 14 dias"}
              {!loading && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />}
            </button>
          </form>

          <p className="mt-3 text-center text-xs text-slate-500">
            Já tem conta?{" "}
            <Link to="/login" data-testid="register-to-login" className="font-bold text-blue-600 hover:text-blue-700">Entrar</Link>
          </p>
        </div>
      </motion.div>

      <Dialog open={Boolean(preview)} onOpenChange={(o) => { if (!o) goIn(); }}>
        <DialogContent data-testid="email-preview-modal" className="max-w-md">
          <DialogTitle className="sr-only">Conta criada</DialogTitle>
          <DialogDescription className="sr-only">Confirmação de criação de conta.</DialogDescription>
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600"><MailCheck className="h-6 w-6" /></div>
            <h2 className="font-heading text-lg font-bold text-slate-900">Conta criada! 🎉</h2>
            <p className="mt-1 text-sm text-slate-500">Guarde suas informações de acesso:</p>
          </div>
          {preview && (
            <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 text-left">
              <p className="text-xs text-slate-400">Para: <span className="font-semibold text-slate-600">{preview.to}</span></p>
              <p className="mt-1 text-sm font-bold text-slate-900">{preview.subject}</p>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{preview.body}</p>
              <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50/60 px-3 py-2 text-xs text-blue-700">
                <b>Login:</b> {preview.login}<br />
                <b>Senha:</b> {preview.password_note}
              </div>
            </div>
          )}
          <button data-testid="email-preview-continue" onClick={goIn} className="mt-4 w-full rounded-xl bg-blue-600 py-3 text-sm font-bold text-white hover:bg-blue-700">
            Ir para o sistema
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ icon: Icon, label, testid, value, onChange, type = "text", placeholder }) => (
  <div>
    <label className="text-xs font-semibold text-slate-600">{label}</label>
    <div className="relative mt-1">
      <Icon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
      <Input data-testid={testid} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="h-10 border-slate-200 pl-9 focus-visible:ring-blue-600" />
    </div>
  </div>
);
